import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { DropdownMenuItem } from '@alltrails/denali/components/DropdownMenu';
import CloseCircle from '@alltrails/denali/icons/CloseCircle';
import CloseLoop from '@alltrails/denali/icons/CloseLoop';
import DoubleBack from '@alltrails/denali/icons/DoubleBack';
import HandWaving from '@alltrails/denali/icons/HandWaving';
import Link from '@alltrails/denali/icons/Link';
import Reverse from '@alltrails/denali/icons/Reverse';
import Split from '@alltrails/denali/icons/Split';
import { DropdownMenuTextOption } from '@alltrails/denali/types';
import type { MarkerProperties } from '@alltrails/types/CustomRoute';
import { useAppSelector } from '../../../redux';
import useSegmentActions from '../useSegmentActions';
import useSegmentLinking from '../useSegmentLinking';
import styles from './ContextMenu.module.scss';

type ContextMenuContentProps = MarkerProperties & {
  onClose: () => void;
};

type ContextMenuOption = (DropdownMenuTextOption | { sectionDivider: true }) & {
  optionKey: string;
};

const getOptionKey = (option: string) => `context-menu-${option}`;
const getSectionDividerKey = (index: number) => `context-menu-divider-${index}`;

const ContextMenuContent = ({ isStart, isEnd, onClose, position }: ContextMenuContentProps) => {
  const selectedHandleIndex = useAppSelector(state => state.customRoute.selectedHandleIndex);
  const {
    isReverseRouteDisabled,
    isReverseSegmentDisabled,
    isDoubleBackDisabled,
    isCloseLoopDisabled,
    reverseRoute,
    reverseSegment,
    doubleBack,
    closeLoop,
    selectStartPoint,
    selectEndPoint,
    removePoint,
    splitSegmentAtHandle
  } = useSegmentActions();

  const { linkToNextSegment, linkToPreviousSegment, unlinkFromNextSegment, unlinkFromPreviousSegment } = useSegmentLinking();

  const linkingOption = useMemo<ContextMenuOption | undefined>(() => {
    const linkToPreviousSegmentOption = linkToPreviousSegment
      ? {
          optionKey: 'context-menu-link-to-previous-segment',
          leadingIcon: { icon: { Component: Link } },
          title: <FormattedMessage defaultMessage="Connect to previous segment" />,
          onClick: () => {
            linkToPreviousSegment();
            onClose();
          }
        }
      : undefined;

    const unlinkFromPreviousSegmentOption = unlinkFromPreviousSegment
      ? {
          optionKey: 'context-menu-unlink-from-previous-segment',
          leadingIcon: { icon: { Component: Link } },
          title: <FormattedMessage defaultMessage="Disconnect segment" />,
          onClick: () => {
            unlinkFromPreviousSegment();
            onClose();
          }
        }
      : undefined;

    const linkToNextSegmentOption = linkToNextSegment
      ? {
          optionKey: 'context-menu-link-to-next-segment',
          leadingIcon: { icon: { Component: Link } },
          title: <FormattedMessage defaultMessage="Connect to next segment" />,
          onClick: () => {
            linkToNextSegment();
            onClose();
          }
        }
      : undefined;

    const unlinkFromNextSegmentOption = unlinkFromNextSegment
      ? {
          optionKey: 'context-menu-unlink-from-next-segment',
          leadingIcon: { icon: { Component: Link } },
          title: <FormattedMessage defaultMessage="Disconnect segment" />,
          onClick: () => {
            unlinkFromNextSegment();
            onClose();
          }
        }
      : undefined;

    if (isStart && isEnd) {
      if (selectedHandleIndex === 0) {
        return linkToPreviousSegmentOption ?? unlinkFromPreviousSegmentOption;
      }
      return linkToNextSegmentOption ?? unlinkFromNextSegmentOption;
    }

    if (isStart) {
      return linkToPreviousSegmentOption ?? unlinkFromPreviousSegmentOption;
    }

    if (isEnd) {
      return linkToNextSegmentOption ?? unlinkFromNextSegmentOption;
    }

    return undefined;
  }, [isEnd, isStart, linkToNextSegment, linkToPreviousSegment, onClose, selectedHandleIndex, unlinkFromNextSegment, unlinkFromPreviousSegment]);

  const optionGroups = useMemo<ContextMenuOption[][]>(() => {
    // Shared options
    const reverseRouteOption = {
      optionKey: getOptionKey('reverse-route'),
      leadingIcon: { icon: { Component: Reverse } },
      title: <FormattedMessage defaultMessage="Reverse all segments" />,
      onClick: async () => {
        await reverseRoute('contextMenu');
        onClose();
      }
    };
    const reverseOption = {
      optionKey: getOptionKey('reverse-segment'),
      leadingIcon: { icon: { Component: Reverse } },
      title: <FormattedMessage defaultMessage="Reverse selected segment" />,
      onClick: async () => {
        await reverseSegment('contextMenu');
        onClose();
      },
      disabled: isReverseSegmentDisabled
    };

    // Hide the reverse route option when disabled rather than showing a disabled option
    const reverseOptions: ContextMenuOption[] = isReverseRouteDisabled ? [reverseOption] : [reverseRouteOption, reverseOption];

    const doubleBackOption = {
      optionKey: getOptionKey('double-back'),
      leadingIcon: { icon: { Component: DoubleBack } },
      title: <FormattedMessage defaultMessage="Double back" />,
      onClick: async () => {
        await doubleBack('contextMenu');
        onClose();
      },
      disabled: isDoubleBackDisabled
    };
    const closeLoopOption = {
      optionKey: getOptionKey('close-loop'),
      leadingIcon: { icon: { Component: CloseLoop } },
      title: <FormattedMessage defaultMessage="Close loop" />,
      onClick: async () => {
        await closeLoop('contextMenu');
        onClose();
      },
      disabled: isCloseLoopDisabled
    };
    const removePointOption = {
      optionKey: getOptionKey('remove-point'),
      leadingIcon: { icon: { Component: CloseCircle } },
      title: <FormattedMessage defaultMessage="Remove point" />,
      onClick: () => {
        removePoint();
        onClose();
      }
    };

    if (isStart && isEnd) {
      const selectPointOption =
        selectedHandleIndex !== 0
          ? {
              optionKey: getOptionKey('select-start'),
              leadingIcon: { icon: { Component: HandWaving } },
              title: <FormattedMessage defaultMessage="Select starting point" />,
              onClick: () => {
                selectStartPoint();
                onClose();
              }
            }
          : {
              optionKey: getOptionKey('select-end'),
              leadingIcon: { icon: { Component: HandWaving } },
              title: <FormattedMessage defaultMessage="Select endpoint" />,
              onClick: () => {
                selectEndPoint();
                onClose();
              }
            };

      const startEndOptions = [reverseOptions, [removePointOption], [selectPointOption]];
      if (linkingOption) {
        startEndOptions.unshift([linkingOption]);
      }
      return startEndOptions;
    }

    if (isStart || isEnd) {
      const startEndOptions = [reverseOptions, [doubleBackOption], [closeLoopOption], [removePointOption]];
      if (linkingOption) {
        startEndOptions.unshift([linkingOption]);
      }
      return startEndOptions;
    }

    const splitSegmentOption = splitSegmentAtHandle
      ? {
          optionKey: getOptionKey('split-segment'),
          leadingIcon: { icon: { Component: Split } },
          title: <FormattedMessage defaultMessage="Split here" />,
          onClick: () => {
            splitSegmentAtHandle();
            onClose();
          }
        }
      : undefined;

    return position !== 'both' ? [[removePointOption, ...(splitSegmentOption ? [splitSegmentOption] : [])]] : [];
  }, [
    closeLoop,
    doubleBack,
    isCloseLoopDisabled,
    isDoubleBackDisabled,
    isEnd,
    isReverseRouteDisabled,
    isReverseSegmentDisabled,
    isStart,
    linkingOption,
    onClose,
    removePoint,
    reverseRoute,
    reverseSegment,
    selectEndPoint,
    selectStartPoint,
    selectedHandleIndex,
    splitSegmentAtHandle,
    position
  ]);

  const options = useMemo(() => {
    const showSectionDividers = optionGroups.length > 1 && optionGroups.some(group => group.length > 1);
    if (showSectionDividers) {
      return optionGroups.reduce((acc, group, index) => {
        acc.push(...group);
        if (index < optionGroups.length - 1) {
          acc.push({ optionKey: getSectionDividerKey(index), sectionDivider: true });
        }
        return acc;
      }, [] as ContextMenuOption[]);
    }
    return optionGroups.flat();
  }, [optionGroups]);

  return (
    <>
      {options.map(({ optionKey, ...option }, index) => {
        if ('sectionDivider' in option) {
          if (index === 0 || index === options.length - 1) {
            return null;
          }
          return <div key={optionKey} className={styles.sectionDivider} />;
        }
        return <DropdownMenuItem key={optionKey} option={option} textVariant="text100" />;
      })}
    </>
  );
};

export default ContextMenuContent;
